import {pick} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';

import BenchlingConfig from 'BenchlingConfig';
import {ClientId} from 'notebook/ELNConstants';
import {isMixtureGroup} from 'notebook/integrations/registry/MixtureGroupConstants';
import {getMixtureGroupAdapter} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {NoteLinkedGroup, NoteLinkedGroupKind} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {reduceGroupUpdateNotePart} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {setTableContent} from 'notebook/notes/plugins/plain-table/TablePlugin';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import {setStructuredTableContent} from 'notebook/structured-tables/StructuredTablePlugin';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {
  GenericStructuredTable,
  NoteLinkedObjectType,
  NotePart,
  NotePartType,
} from 'notebook/util/NoteUtilConstants';
import {insertOrRemoveProcedureConditionReplicateColumn} from 'procedures/tables/ProcedureConditionReplicateColumn';
import * as logging from 'util/Logging';

export interface AugmentNotePartsContext {
  fileId: string;
  isProcedureMultibatchExecution: boolean;
}

/**
 * A series of functions that are performed on new NoteParts to transform them as needed to match the
 * editor environment they are inserted into.
 */
export default function augmentNoteParts(
  noteParts: Array<NotePart>,
  context: AugmentNotePartsContext
): Array<NotePart> {
  let newNoteParts = noteParts;
  if (BenchlingConfig.PROCEDURES_ENABLE) {
    newNoteParts = insertOrRemoveProcedureConditionReplicateColumn(
      newNoteParts,
      pick(context, 'fileId', 'isProcedureMultibatchExecution')
    );
  }
  return newNoteParts;
}

/** Take a TableMap and a series of NoteParts and replace the NoteParts with the updated Tables in the
 * TableMap */
export function updateTableNotePartsFromTableMap(
  tableMap: FullTableMap,
  noteParts: Array<NotePart>
): Array<NotePart> {
  const newNoteParts = noteParts.map((notePart): NotePart => {
    const notePartType = getNotePartType(notePart);
    switch (notePartType) {
      case NotePartType.TABLE:
        if (!notePart.table) {
          logging.error('Expected Table NotePart to have a table.', {extra: {notePart}});
          return notePart;
        }
        return reactUpdate(notePart, setTableContent({...notePart, table: tableMap[notePart.table.name]}));
      case NoteLinkedObjectType.STRUCTURED_TABLE: {
        if (!notePart.item) {
          logging.error('Expected StructuredTable NotePart to have a StructuredTable.', {extra: {notePart}});
          return notePart;
        }
        const structuredTable = notePart.item as GenericStructuredTable<ClientId>;
        return reactUpdate(notePart, setStructuredTableContent({table: tableMap[structuredTable.name]}));
      }
      case NoteLinkedObjectType.NOTE_LINKED_GROUP: {
        const group = notePart.item as NoteLinkedGroup<unknown>;

        if (
          group.kind === NoteLinkedGroupKind.NOTE_PART_GRID ||
          group.kind === NoteLinkedGroupKind.STOICHIOMETRY_GROUP
        ) {
          return notePart;
        }
        if (!isMixtureGroup(group)) {
          logging.error('Unexpected NoteLinkedGroup kind.', {
            extra: {kind: group.kind, item: notePart.item},
          });
          return notePart;
        }
        const metadataTableNotePart = getMixtureGroupAdapter().getMixtureMetadataNotePart(group);
        const updatedMetadataTableNotePart = reactUpdate(
          metadataTableNotePart,
          setStructuredTableContent({
            table: tableMap[metadataTableNotePart.item.name],
          })
        );
        const newItem = reduceGroupUpdateNotePart(group, updatedMetadataTableNotePart);
        return {
          ...notePart,
          item: newItem,
        };
      }
      default:
        return notePart;
    }
  });
  return newNoteParts;
}
